// src/pages/Automations/components/AutomationCategory.jsx
// Expandable category accordion with triggers (Light theme)

import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import AutomationTrigger from './AutomationTrigger';

// Light theme color mappings
const LIGHT_COLORS = {
  blue: { bg: 'bg-blue-50', icon: 'text-blue-600', badge: 'bg-blue-100 text-blue-700', border: 'border-blue-200' },
  purple: { bg: 'bg-purple-50', icon: 'text-purple-600', badge: 'bg-purple-100 text-purple-700', border: 'border-purple-200' },
  green: { bg: 'bg-green-50', icon: 'text-green-600', badge: 'bg-green-100 text-green-700', border: 'border-green-200' },
  amber: { bg: 'bg-amber-50', icon: 'text-amber-600', badge: 'bg-amber-100 text-amber-700', border: 'border-amber-200' },
  red: { bg: 'bg-red-50', icon: 'text-red-600', badge: 'bg-red-100 text-red-700', border: 'border-red-200' },
  cyan: { bg: 'bg-cyan-50', icon: 'text-cyan-600', badge: 'bg-cyan-100 text-cyan-700', border: 'border-cyan-200' },
  pink: { bg: 'bg-pink-50', icon: 'text-pink-600', badge: 'bg-pink-100 text-pink-700', border: 'border-pink-200' },
};

export function AutomationCategory({
  category,
  enabledCount,
  isEnabled,
  onToggle,
  onEditTemplate,
  templates,
  defaultExpanded = false,
  pausedTriggers = []  // List of trigger keys that are paused globally
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const colors = LIGHT_COLORS[category.color] || LIGHT_COLORS.blue;
  const Icon = category.icon;
  const totalTriggers = category.triggers.length;

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
      {/* Category Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors`}
      >
        <div className="flex items-center gap-4">
          {/* Icon */}
          <div className={`p-2.5 rounded-xl ${colors.bg} ${colors.icon}`}>
            <Icon className="w-5 h-5" />
          </div>

          {/* Title & Description */}
          <div className="text-left">
            <h3 className="font-semibold text-gray-800 text-lg">{category.name}</h3>
            <p className="text-sm text-gray-500 hidden sm:block">{category.description}</p>
          </div>
        </div>

        {/* Right Side */}
        <div className="flex items-center gap-4">
          {/* Count Badge */}
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${colors.badge}`}>
            {enabledCount} / {totalTriggers} active
          </div>

          {/* Expand Icon */}
          {expanded ? (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </button>

      {/* Triggers List */}
      {expanded && (
        <div className="p-5 pt-0 space-y-3 border-t border-gray-100">
          {category.triggers.map((trigger) => (
            <AutomationTrigger
              key={trigger.key}
              trigger={trigger}
              enabled={isEnabled(trigger.key)}
              onToggle={(enabled) => onToggle(trigger.key, enabled)}
              onEdit={() => onEditTemplate(trigger)}
              template={templates[trigger.key]}
              categoryColor={category.color}
              isPaused={pausedTriggers.includes(trigger.key)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default AutomationCategory;
